import { ErrorBoundary } from '@sentry/react';
import { Tabs as TabsPrimitive } from 'radix-ui';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { integrationStatusConfig } from '@/features/integrations/integration-status-ui';
import { cn } from '@/lib/utils';

import { AccountsTab } from './accounts-tab';
import {
  resolveDesktopConfiguration,
  resolveRemoteProviderTabs,
} from './configuration/configuration.registry';
import { useProviderAccountActions } from './integration-management.actions';
import { useProviderToolsPanel } from './integration-management.query';
import { ProviderHeaderAction } from './provider-header-action';
import { ProviderIcon } from './provider-rail';
import { ToolsTab } from './tools-tab';

import type {
  ConfigurationContext,
  DesktopConfigurationTab,
} from './configuration/configuration.types';
import type {
  IntegrationProvider,
  IntegrationSettings,
  IntegrationStatus,
} from '@littlebird/integrations';
import type { ComponentType } from 'react';

type TabIcon = ComponentType<{ className?: string }>;

// Our own status set mapped onto integrationStatusConfig's styles — reused
// for the visual treatment, not the labels (e.g. 'offline' borrows the
// expired/amber style but keeps its own "Offline" copy).
const STATUS_PILL: Record<
  IntegrationStatus,
  { label: string; className: string }
> = {
  usable: {
    label: 'Connected',
    className: integrationStatusConfig.connected.className,
  },
  connecting: {
    label: 'Connecting…',
    className: integrationStatusConfig.probing.className,
  },
  needs_attention: {
    label: 'Needs attention',
    className: integrationStatusConfig.expired.className,
  },
  offline: {
    label: 'Offline',
    className: integrationStatusConfig.expired.className,
  },
  disabled: {
    label: 'Disabled',
    className: integrationStatusConfig.disabled.className,
  },
};

/** One resolved configuration tab paired with the exact `localSource` that
 * produced it, so the tab's module renders with the right settings entry
 * even when a provider carries more than one (e.g. two local MCP
 * installations grouped under the same device provider).
 *
 * `value` is the Radix tab value / React key — distinct from `tab.id`, which
 * stays the registry's stable identifier. Two settings entries can resolve
 * to the same `tab.id` (e.g. two unknown-template local MCPs both landing on
 * the generic Settings tab), so `value` folds in the entry's own identity to
 * stay unique per entry even then. */
type ConfigurationTabEntry = {
  tab: DesktopConfigurationTab;
  context: ConfigurationContext;
  value: string;
};

function configurationEntryIdentity(localSource: IntegrationSettings): string {
  if (localSource.kind === 'local_mcp') return localSource.installationId;
  if (localSource.kind === 'apple_native') return localSource.toolkit;
  return localSource.channel;
}

function resolveConfigurationTabEntries(
  provider: IntegrationProvider,
  meetingNotesSyncEnabled: boolean
): readonly ConfigurationTabEntry[] {
  const remoteEntries = resolveRemoteProviderTabs(provider, {
    meetingNotesSyncEnabled,
  }).map((tab) => ({
    tab,
    context: { provider, localSource: undefined },
    value: `${tab.id}:${provider.key}`,
  }));
  const localEntries = provider.settings.flatMap((localSource) => {
    const decision = resolveDesktopConfiguration({ provider, localSource });
    return decision.kind === 'tabs'
      ? decision.tabs.map((tab) => ({
          tab,
          context: { provider, localSource },
          value: `${tab.id}:${configurationEntryIdentity(localSource)}`,
        }))
      : [];
  });
  return [...localEntries, ...remoteEntries];
}

function UnderlineTab({
  value,
  icon: Icon,
  label,
  count,
}: {
  value: string;
  icon?: TabIcon;
  label: string;
  count?: number;
}) {
  return (
    <TabsPrimitive.Trigger
      value={value}
      // The visual count badge is aria-hidden (below) so it doesn't read as
      // e.g. "Tools 3" merged into the label with no separator — this
      // aria-label restores the count to the accessible name properly.
      aria-label={count !== undefined ? `${label} (${count})` : undefined}
      className={cn(
        'flex items-center gap-2 px-3.5 py-2.5 -mb-px text-sm',
        'border-b-2 border-transparent transition-colors',
        'text-foreground-muted hover:text-foreground-subtle',
        'data-[state=active]:text-foreground data-[state=active]:border-foreground data-[state=active]:font-medium'
      )}
    >
      {Icon && <Icon className="size-3.5" />}
      {label}
      {count !== undefined && (
        // Hidden from the accessibility tree — the aria-label above already
        // carries the count in a screen-reader-friendly form.
        <span
          aria-hidden="true"
          className="text-xs font-mono text-foreground-muted bg-secondary rounded-full px-1.5"
        >
          {count}
        </span>
      )}
    </TabsPrimitive.Trigger>
  );
}

const CONFIGURATION_CRASH_FALLBACK = (
  <YStack className="items-center gap-3 p-10 text-center">
    <Icons.warn className="size-5 text-foreground-muted" />
    <p className="text-sm text-foreground-muted">
      This section couldn&apos;t load.
    </p>
  </YStack>
);

/** A configuration module crash stays inside its own panel — the rest of the
 * tab strip and the other panels keep working. */
function ConfigurationTabPanel({ entry }: { entry: ConfigurationTabEntry }) {
  const Component = entry.tab.Component;
  return (
    <ErrorBoundary fallback={CONFIGURATION_CRASH_FALLBACK}>
      <Component context={entry.context} />
    </ErrorBoundary>
  );
}

export function ProviderDetail({
  provider,
}: {
  provider: IntegrationProvider;
}) {
  const hasFeature = useFeature();
  // canAdd requires the actions hook (multi-account flags); accounts.length
  // is already on the provider.
  const { canAdd } = useProviderAccountActions(provider);
  const showTools =
    provider.hasTools && hasFeature(FEATURES.UNIFIED_INTEGRATIONS_MANAGEMENT);
  // Called unconditionally (rules of hooks) — the query inside gates its own
  // fetch on `showTools`, so it starts as soon as this provider is
  // selected, not only once the user clicks into the Tools tab (which Radix
  // would otherwise leave unmounted until then).
  const panel = useProviderToolsPanel(provider, showTools);
  const showAccounts = provider.accounts.length > 0 || canAdd;
  const showStatusPill =
    provider.status !== 'usable' || provider.accounts.length === 0;
  // The switch only means something to users whose notes the backend syncs
  const configurationEntries = resolveConfigurationTabEntries(
    provider,
    hasFeature(FEATURES.EXTERNAL_MEETING_NOTES)
  );
  const tabValues = [
    ...(showAccounts ? ['accounts'] : []),
    ...configurationEntries.map((entry) => entry.value),
    ...(showTools ? ['tools'] : []),
  ];
  const showTabStrip = tabValues.length > 1;

  return (
    <YStack className="h-full min-h-0">
      <YStack className="shrink-0 gap-3 px-8 pt-8 pb-4">
        <ProviderIcon provider={provider} className="size-10 shrink-0" />
        <XStack className="items-start justify-between gap-4 min-w-0">
          <YStack className="flex-1 gap-1 min-w-0">
            <XStack className="gap-2 items-center min-w-0">
              <p className="text-foreground font-medium truncate">
                {provider.name}
              </p>
              {showStatusPill && (
                <span
                  className={cn(
                    'text-xs rounded-full px-2 py-0.5 shrink-0',
                    STATUS_PILL[provider.status].className
                  )}
                >
                  {STATUS_PILL[provider.status].label}
                </span>
              )}
            </XStack>
            {provider.description && (
              <p className="text-foreground-muted text-xs line-clamp-1">
                {provider.description}
              </p>
            )}
          </YStack>
          <ProviderHeaderAction provider={provider} />
        </XStack>
      </YStack>
      {tabValues.length > 0 && (
        // Uncontrolled Tabs ignores a later `defaultValue` change, so the
        // caller must key ProviderDetail itself by provider — that remount
        // covers Tabs' defaultValue along with the rest of this component's
        // per-provider state (see integration-management-settings.tsx).
        <Tabs defaultValue={tabValues[0]} className="flex-1 min-h-0 gap-0">
          {showTabStrip && (
            <TabsPrimitive.List className="flex shrink-0 items-center gap-1 border-b border-border px-8">
              {showAccounts && (
                <UnderlineTab
                  value="accounts"
                  label="Accounts"
                  count={provider.accounts.length}
                />
              )}
              {configurationEntries.map((entry) => (
                <UnderlineTab
                  key={entry.value}
                  value={entry.value}
                  icon={entry.tab.icon}
                  label={entry.tab.label}
                />
              ))}
              {showTools && (
                <UnderlineTab
                  value="tools"
                  icon={Icons.slidersHorizontal}
                  label="Tools"
                  count={
                    panel.isInitialLoading ? undefined : panel.tools.length
                  }
                />
              )}
            </TabsPrimitive.List>
          )}
          {showAccounts && (
            <TabsContent
              value="accounts"
              className="mt-0 flex-1 min-h-0 overflow-y-auto [scrollbar-gutter:stable]"
            >
              <AccountsTab provider={provider} />
            </TabsContent>
          )}
          {configurationEntries.map((entry) => (
            <TabsContent
              key={entry.value}
              value={entry.value}
              className="mt-0 flex-1 min-h-0 overflow-y-auto [scrollbar-gutter:stable]"
            >
              <ConfigurationTabPanel entry={entry} />
            </TabsContent>
          ))}
          {showTools && (
            <TabsContent
              value="tools"
              className="mt-0 flex-1 min-h-0 overflow-y-auto [scrollbar-gutter:stable]"
            >
              <ToolsTab
                tools={panel.tools}
                discovery={panel.discovery}
                isInitialLoading={panel.isInitialLoading}
                loadErrorMessage={panel.loadErrorMessage}
                isRefetchError={panel.isRefetchError}
                retry={panel.retry}
                onSetToolPermission={panel.setToolPermission}
                onSetBehaviorPermission={panel.setBehaviorPermission}
                failureMessage={panel.failure?.message ?? null}
              />
            </TabsContent>
          )}
        </Tabs>
      )}
    </YStack>
  );
}
